import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  Target, 
  Heart, 
  Zap,
  Users,
  Rocket,
  Sparkles,
  ArrowLeft,
  PartyPopper,
  Coffee
} from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  const values = [
    {
      icon: Rocket,
      title: "Built Different",
      description: "We're not your parents' ride-share. We're building something fresh, fair, and actually fun to use."
    },
    {
      icon: Heart,
      title: "Real People, Real Rides",
      description: "Drivers keep 85% of every fare. No corporate greed, just people helping people get around."
    },
    {
      icon: Zap,
      title: "Tech That Slaps",
      description: "Real-time tracking, smart matching, instant payments. We built the app we actually wanted to use."
    },
    {
      icon: Users,
      title: "Community First",
      description: "Whether you're catching a ride to class or driving between shifts, you're part of something bigger."
    }
  ];

  const whyWeBuiltThis = [
    {
      icon: Coffee,
      title: "Late Night Cravings",
      description: "Ever tried getting a ride at 2 AM when you're starving? Yeah, us too. We fixed that."
    },
    {
      icon: PartyPopper,
      title: "Split the Fare",
      description: "Going out with friends shouldn't mean doing math at 1 AM. Easy fare splitting, no drama."
    },
    {
      icon: Sparkles,
      title: "Actually Affordable",
      description: "We're not trying to make billions. We're trying to help you afford groceries AND get there."
    }
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-['Barlow_Condensed'] font-black text-white uppercase mb-6">
            We're Just Getting Started
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Reddi is the ride-share app built by people who were tired of the same old thing. 
            Fresh out the gate in 2025, and yeah—we're doing things differently.
          </p>
        </div>
      </section>

      {/* The Vibe */}
      <section className="py-12 px-6 md:px-12 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-bold text-white">
            "We built Reddi because we got tired of apps that treat drivers like numbers 
            and charge riders like they're made of money."
          </p>
          <p className="text-red-100 mt-4">— The Reddi Team</p>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4 text-center">
            What We're About
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            No corporate speak. No buzzwords. Just the stuff that actually matters.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 p-8 text-center hover:border-red-500/50 transition-colors"
              >
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We Built This */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4 text-center">
            Why We Built This
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Real problems we actually had. Real solutions we actually built.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {whyWeBuiltThis.map((item, index) => (
              <div 
                key={index}
                className="text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-6 transform rotate-3 hover:rotate-0 transition-transform">
                  <item.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Story */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-8 text-center">
            The Story
          </h2>
          <div className="prose prose-invert max-w-none text-center">
            <p className="text-slate-400 text-lg leading-relaxed mb-6">
              Look, we're not going to pretend we've been around forever or that we've 
              "disrupted" anything yet. We're a brand new app, launched in 2025, built by 
              people who were just frustrated with their options.
            </p>
            <p className="text-slate-400 text-lg leading-relaxed mb-6">
              Tired of surge pricing when you're just trying to get home from the library. 
              Tired of watching drivers get pennies while platforms take the bag. 
              Tired of clunky apps that feel like they were designed in 2010.
            </p>
            <p className="text-slate-400 text-lg leading-relaxed">
              So we built something new. Something that actually works for everyone. 
              We're small, we're hungry, and we're ready to prove ourselves. 
              Come grow with us.
            </p>
          </div>
        </div>
      </section>

      {/* Join Us */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Target className="w-12 h-12 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-6">
            Be Part of Something New
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">
            We're building this thing from the ground up, and early supporters get the love. 
            Driver bonuses, rider perks, and the satisfaction of being here first.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-r from-red-600/20 to-red-900/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-6">
            Ready to Roll?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?type=rider">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Get Your First Ride
              </Button>
            </Link>
            <Link to="/careers">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg">
                Join the Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default About;
